import { syncPinShapes } from './syncPinShapes';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import { getActivityId } from './autoLayout';
import { boardConfirm } from './boardAlert';
import { isEraserToolActive } from './canvasToolState';
import { flushBoardDocumentOps } from './persistBoardDocumentOps';
import { unmountShapesThen } from './pendingShapeUnmount';

/** While true, pin-card beforeDelete handlers must allow programmatic shape removal. */
export const pinCardCascadeDeleting = { active: false };

/**
 * Collect pin-cards and related AI annotations (text/arrows) for the given activity ids.
 */
export function collectRelatedShapesForPins(editor, activityIds) {
  const ids = new Set((activityIds || []).map(String));
  if (!ids.size || !editor) return [];
  const related = [];
  for (const shape of editor.getCurrentPageShapes()) {
    const metaId = shape.meta?.activityId != null ? String(shape.meta.activityId) : null;
    if (metaId && ids.has(metaId)) {
      related.push(shape.id);
      continue;
    }
    const sid = String(shape.id);
    for (const activityId of ids) {
      if (
        sid.includes(`ai-text-${activityId}`) ||
        (sid.includes('ai-arrow-') && sid.includes(activityId))
      ) {
        related.push(shape.id);
        break;
      }
    }
  }
  return related;
}

/**
 * Confirm permanent deletion of one or more user pins.
 * If `onConfirm` is provided it runs inside the dialog (with a loading spinner)
 * before the dialog closes.
 * @param {number} count
 * @param {(() => Promise<void>) | undefined} onConfirm
 */
export async function confirmDeleteUserPins(count = 1, onConfirm) {
  const n = Math.max(1, Number(count) || 1);
  const title = n === 1 ? 'Delete this pin?' : `Delete ${n} pins?`;
  const message =
    n === 1
      ? 'This pin will be permanently deleted and cannot be undone.'
      : 'These pins will be permanently deleted and cannot be undone.';
  return boardConfirm({
    title,
    message,
    confirmLabel: n === 1 ? 'Delete' : `Delete ${n}`,
    cancelLabel: 'Cancel',
    danger: true,
    onConfirm: typeof onConfirm === 'function' ? onConfirm : undefined,
  });
}

/**
 * Delete user pins from DB (activity + comment + screenshots) and remove related canvas shapes.
 */
export async function deleteUserPins(
  editor,
  activityIds,
  {
    deleteActivities,
    activities,
    board,
    updatePlacementsLocal,
    saveSnapshot,
    confirm = false,
    onDeleted = null,
  } = {}
) {
  const ids = [...new Set((activityIds || []).map(String).filter(Boolean))];
  if (!ids.length || !editor) return [];

  let deleted = ids;

  if (confirm) {
    // Pass the network call as onConfirm so the dialog shows a spinner
    // while the deletion is in flight, then closes when done.
    const networkDeleteRef = { result: null };
    const confirmed = await confirmDeleteUserPins(ids.length, async () => {
      if (deleteActivities) {
        networkDeleteRef.result = await deleteActivities(ids);
        deleted = networkDeleteRef.result;
      }
    });
    if (!confirmed) return [];
    // If onConfirm wasn't executed (no host mounted), fall back to calling directly.
    if (networkDeleteRef.result === null && deleteActivities) {
      deleted = await deleteActivities(ids);
    }
  } else if (deleteActivities) {
    deleted = await deleteActivities(ids);
  }

  if (!deleted.length) return [];

  const deletedSet = new Set(deleted.map(String));
  const workingActivities = (activities || []).filter(a => !deletedSet.has(getActivityId(a)));
  const nextPlacements = (board?.placements || []).filter(
    p => !deletedSet.has(String(p.activityId))
  );
  const nextBoard = { ...(board || {}), placements: nextPlacements };
  updatePlacementsLocal?.(nextPlacements);

  const before = extractBoardDocumentSnapshot(editor);
  pinCardCascadeDeleting.active = true;
  try {
    const related = collectRelatedShapesForPins(editor, [...deletedSet]);
    unmountShapesThen(related, () => {
      editor.run(() => {
        editor.markHistoryStoppingPoint('before-delete-user-pins');
        if (related.length) editor.deleteShapes(related);
        syncPinShapes(editor, workingActivities, nextBoard);
      });
    });
  } finally {
    pinCardCascadeDeleting.active = false;
  }

  // Explicit remove ops for related annotations (arrows/texts). Pin-cards are
  // placements-only and never live in tldrawSnapshot.
  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      await flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }

  try {
    onDeleted?.(deleted);
  } catch {
    /* ignore digest hooks */
  }

  return deleted;
}

/**
 * Intercept tldraw Delete/Backspace on pin-cards and run full cascade delete instead of
 * shape-only removal (which would orphan Activity/Comment/screenshot docs).
 *
 * Shape-only removals after the activity is already gone from the store (e.g. syncPinShapes)
 * are allowed through.
 */
export function registerUserPinDeleteCascade(
  editor,
  { onDeleteUserPins, activityStillExists } = {}
) {
  let pendingActivityIds = null;
  let scheduled = false;

  return editor.sideEffects.registerBeforeDeleteHandler('shape', (shape, source) => {
    if (source !== 'user') return;
    if (pinCardCascadeDeleting.active) return;
    if (shape?.type !== 'pin-card') return;
    // Eraser deletes are routed through deleteCanvasShapes; do not cascade here.
    if (isEraserToolActive(editor)) return;
    if (editor.getIsReadonly?.() || editor.getInstanceState()?.isReadonly) return false;

    const activityId = shape.meta?.activityId;
    if (!activityId) return false;

    // If the activity is already gone, allow normal shape cleanup (post-delete sync).
    if (activityStillExists && !activityStillExists(activityId)) return;

    if (!pendingActivityIds) pendingActivityIds = new Set();
    pendingActivityIds.add(String(activityId));

    if (!scheduled) {
      scheduled = true;
      queueMicrotask(() => {
        const ids = [...(pendingActivityIds || [])];
        pendingActivityIds = null;
        scheduled = false;
        if (!ids.length || !onDeleteUserPins) return;
        Promise.resolve(onDeleteUserPins(ids)).catch(err => {
          console.warn('Failed to cascade-delete user pins', err);
        });
      });
    }

    return false;
  });
}
